import React, { createContext, useContext, useState } from "react";

import { useAlert } from "../Alert/AlertContext";
import { useAuth } from "../Auth/AuthProvider";
import { getData, postData } from "../../Services/apiService";
import { useTheme } from "../Theme/themeContext";
import { useTranslation } from "react-i18next";

const Config = createContext();

export const useConfig = () => useContext(Config);

/**
 * ConfigProvider component that provides configuration context to its children.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The rendered component.
 */
export const ConfigProvider = ({ children }) => {
    /**
     * Initializes the state with configuration data fetched from the server.
     *
     * @returns {Array} The configuration data.
     */
    const [configs, setConfigs] = useState({});
    const { addAlert } = useAlert(); // Hook para exibir alertas
    const { auth } = useAuth(); // Hook para autenticação
    const { setTheme } = useTheme(); // Hook para tema
    const { i18n } = useTranslation(); // Hook para tradução

    /**
     * Updates the configuration value for a given key.
     *
     * This function updates the configuration value in the database and then updates the local state.
     *
     * @param {string} key - The configuration key to update.
     * @param {*} value - The new value to set for the configuration key.
     * @returns {void}
     */
    const setConfig = async (key, value) => {
        try {
            const pushConfig = await postData(`/user/${auth.user.id}/config`, {
                key: key,
                value: value,
            });

            if (pushConfig.status !== 200) {
                addAlert("danger", pushConfig.status, pushConfig.data);
                return;
            }

            // Atualiza o idioma ou tema no localStorage
            if (key === "language") {
                localStorage.setItem(key, value);
            }

            // Atualiza o valor no estado
            setConfigs((prevConfigs) => ({
                ...prevConfigs,
                [key]: value,
            }));
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Asynchronously loads the user's configuration from the server.
     * Fetches configuration data for the authenticated user and updates the state.
     * Displays an alert if the request fails or an error occurs.
     *
     * @async
     * @function loadingConfig
     * @returns {Promise<void>} Resolves when the configuration is loaded and state is updated.
     */
    const loadingConfig = async () => {
        try {
            const gettingConfig = await getData(
                `/user/${auth.user.id}/config`,
                {}
            );
            if (gettingConfig.status !== 200) {
                addAlert("danger", gettingConfig.status, gettingConfig.data);
                return;
            }

            console.log(gettingConfig.body);

            for (let config of gettingConfig.body) {
                // Atualiza o idioma ou tema no localStorage
                switch (config.key) {
                    case "language":
                        localStorage.setItem(config.key, config.value);
                        i18n.changeLanguage(config.value);
                        document.documentElement.setAttribute(
                            "lang",
                            config.value
                        );
                        break;
                    case "theme":
                        localStorage.setItem(config.key, config.value);
                        setTheme(config.value);
                        break;
                    default:
                        break;
                }

                // Atualiza o valor no estado
                setConfigs((prevConfigs) => ({
                    ...prevConfigs,
                    [config.key]: config.value,
                }));
            }
        } catch (error) {
            addAlert("danger", error.message, "Erro ao carregar configurações");
        }
    };

    // Carrega as configurações ao iniciar o componente
    React.useEffect(() => {
        loadingConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth]);

    return (
        <>
            <Config.Provider
                value={{
                    configs,
                    setConfig,
                }}
            >
                {children}
            </Config.Provider>
        </>
    );
};
